import { ref, onBeforeUnmount } from 'vue'
import type { DictationLang } from '@/types/user.type'

// SpeechRecognition 本体の型は TS の lib.dom に無いため、使う分だけ最小宣言する（周辺型は lib 由来）
interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  start: () => void
  stop: () => void
  onresult: ((e: SpeechRecognitionEvent) => void) | null
  onerror: (() => void) | null
  onend: (() => void) | null
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

const DICTATION_BCP47: Record<DictationLang, string> = {
  en: 'en-US',
  ja: 'ja-JP',
  ko: 'ko-KR',
  zh: 'zh-CN',
}

const speechCtor: SpeechRecognitionCtor | null = (() => {
  const w = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
})()

interface UseDictationOptions {
  // 録音開始時点の入力欄の現在値。認識結果はこの後ろに追記していく
  getBaseText: () => string
  // 認識結果（ベース + transcript）を入力欄へ書き戻す
  onTranscript: (text: string) => void
  // 音声入力言語設定。未設定は en
  lang: () => DictationLang | undefined
  // 録音開始を抑止する条件（送信中など）。省略時は常に開始可
  canStart?: () => boolean
}

export function useDictation(options: UseDictationOptions) {
  const isVoiceSupported = speechCtor !== null
  const isRecording = ref(false)
  let recognition: SpeechRecognitionLike | null = null

  function toggleRecording() {
    if (isRecording.value) {
      recognition?.stop()
      return
    }
    if (!speechCtor || options.canStart?.() === false) return
    const rec = new speechCtor()
    rec.lang = DICTATION_BCP47[options.lang() ?? 'en']
    rec.continuous = true
    rec.interimResults = true
    const base = options.getBaseText()
    const dictationBase = base.trim() ? `${base.trimEnd()} ` : ''
    rec.onresult = (e) => {
      let transcript = ''
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript
      }
      options.onTranscript(dictationBase + transcript)
    }
    rec.onerror = stopRecording
    rec.onend = stopRecording
    recognition = rec
    isRecording.value = true
    rec.start()
  }

  function stopRecording() {
    const rec = recognition
    recognition = null
    isRecording.value = false
    rec?.stop()
  }

  onBeforeUnmount(stopRecording)

  return { isVoiceSupported, isRecording, toggleRecording, stopRecording }
}
